import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  ContentCopy,
  DeleteOutline,
} from '@mui/icons-material';
import { DocumentTextEditor } from './DocumentTextEditor';
import { IEditorNode } from './editorModel';
import { IRichParagraph, paragraphsOfNode } from './richTextModel';

/**
 * Ein Text-Element, bearbeitet an Ort und Stelle in der Leseansicht (#1245, Teil 48).
 *
 * Vorher öffnete ein Klick in den Text den Detailbereich rechts, und dort stand derselbe Text ein
 * zweites Mal, zerlegt in einen Kasten je Absatz. Jetzt wird das angeklickte Element selbst zum
 * Editor — dieselbe Stelle, dieselbe Schrift. Immer nur eines: ein Abschnitt besteht auch aus
 * Bedingungen, Fragen und Tabellen, und die bleiben ruhig, bis man sie anklickt.
 */

interface Props {
  node: IEditorNode;
  onChange: (_paragraphs: IRichParagraph[]) => void;
  onDone: () => void;
  questions?: { questionId: string; prompt: string; answerType: string }[];
  idLegalTransaction?: number | null;
  /** Nur für Elemente direkt im Abschnitt — verschachtelte ordnet der Detailbereich. */
  onMove?: (_delta: number) => void;
  onDuplicate?: () => void;
  onRemove?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  /** Wo geklickt wurde. */
  initialCursor?: { paragraph?: number; offset?: number };
  /** Datenfelder einfügen (in einer fertigen Urkunde nicht). */
  allowFields?: boolean;
  /** Hinweis unter dem Editor. */
  hint?: string;
}

export const InlineTextEdit: React.FC<Props> = ({
  node,
  onChange,
  onDone,
  questions,
  idLegalTransaction,
  onMove,
  onDuplicate,
  onRemove,
  canMoveUp,
  canMoveDown,
  initialCursor,
  allowFields,
  hint = 'Esc beendet das Bearbeiten · Speichern oben',
}) => (
  <Box
    onKeyDown={event => {
      if (event.key === 'Escape') {
        // Nur das Bearbeiten beenden — ein umgebendes Fenster (»Urkunde in DiNo erstellen«)
        // schlösse sich sonst mit, samt aller Änderungen.
        event.preventDefault();
        event.stopPropagation();
        onDone();
      }
    }}
    data-testid='inline-text-edit'
  >
    <DocumentTextEditor
      variant='inline'
      autoFocus
      initialCursor={initialCursor}
      allowFields={allowFields}
      paragraphs={paragraphsOfNode(node)}
      onChange={onChange}
      questions={questions}
      idLegalTransaction={idLegalTransaction}
    />
    <Stack
      direction='row'
      spacing={1}
      alignItems='center'
      sx={{ mt: 0.75, fontFamily: 'Roboto, Helvetica, Arial, sans-serif' }}
    >
      <Button size='small' variant='contained' onClick={onDone}>
        Fertig
      </Button>
      <Typography variant='caption' color='text.secondary'>
        {hint}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      {onMove && (
        <>
          <Tooltip title='Element nach oben'>
            <span>
              <IconButton
                size='small'
                disabled={!canMoveUp}
                onClick={() => onMove(-1)}
              >
                <ArrowUpward fontSize='inherit' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Element nach unten'>
            <span>
              <IconButton
                size='small'
                disabled={!canMoveDown}
                onClick={() => onMove(1)}
              >
                <ArrowDownward fontSize='inherit' />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
      {onDuplicate && (
        <Tooltip title='Element duplizieren'>
          <IconButton size='small' onClick={onDuplicate}>
            <ContentCopy fontSize='inherit' />
          </IconButton>
        </Tooltip>
      )}
      {onRemove && (
        <Tooltip title='Element löschen'>
          <IconButton size='small' color='error' onClick={onRemove}>
            <DeleteOutline fontSize='inherit' />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  </Box>
);
